/** Plain words for a report template and its runs: sections, owner, units, period, status. */
import type { BadgeVariant } from '@servicecompressionllc/sc-design-system';
import { formatDay, formatMonth } from '../../lib/format.js';
import type {
  ReportRun,
  ReportSection,
  ReportTemplateDetail,
} from './useReportTemplatePageLogic.js';

const DISPLAY: Record<string, string> = {
  number: 'Headline number',
  table: 'Table',
  bar: 'Bar chart',
  pie: 'Pie chart',
};

/** The line under a section title: what kind of thing it is. */
export const sectionDetail = (section: ReportSection): string => {
  if (section.kind === 'group') {
    const titles = (section.sections ?? []).map((s) => s.title.toLowerCase());
    const list =
      titles.length > 1
        ? `${titles.slice(0, -1).join(', ')} and ${titles.at(-1) ?? ''}`
        : titles[0];
    return list ? `For each unit: ${list}` : 'For each unit';
  }
  if (section.kind === 'narrative') return 'Written summary';
  if (section.kind === 'text') return 'Text';
  return DISPLAY[section.display ?? ''] ?? 'Table';
};

/** "Yours. Published to Service Compression and shared with Charles Crenshaw." */
export const ownerLine = (t: ReportTemplateDetail, meId: string, tenantName: string): string => {
  const owner = t.owner.name ?? 'someone';
  if (t.owner.id !== meId)
    return t.visibility === 'tenant' ? `Published by ${owner}.` : `Shared with you by ${owner}.`;
  const names = t.shares.map((s) => s.name ?? s.email);
  const parts = [
    t.visibility === 'tenant' ? `published to ${tenantName}` : null,
    names.length > 0 ? `shared with ${names.join(', ')}` : null,
  ].filter(Boolean);
  if (parts.length === 0) return 'Yours. Only you can see it.';
  const joined = parts.join(' and ');
  return `Yours. ${joined.charAt(0).toUpperCase()}${joined.slice(1)}.`;
};

const RELATIVE: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  'last 7 days': 'Last 7 days',
  'last 24 hours': 'Last 24 hours',
  'this month': 'This month',
  'last month': 'Last month',
};

/** A run's period in words: "August 2026", "Aug 1 to Aug 31", "Today, Sep 24". */
export const periodLabel = (value: string, ranAt: string): string => {
  const relative = RELATIVE[value.trim().toLowerCase()];
  if (relative)
    return value.trim().toLowerCase() === 'today' ? `${relative}, ${formatDay(ranAt)}` : relative;
  const month = /^(\d{4})-(\d{2})$/.exec(value);
  if (month) return formatMonth(Number(month[1]), Number(month[2]));
  const range = /^(\d{4}-\d{2}-\d{2}) to (\d{4}-\d{2}-\d{2})$/.exec(value);
  if (range?.[1] && range[2])
    return `${formatDay(`${range[1]}T12:00:00`)} to ${formatDay(`${range[2]}T12:00:00`)}`;
  return value;
};

export const RUN_STATUS: Record<ReportRun['status'], { label: string; variant: BadgeVariant }> = {
  queued: { label: 'Queued', variant: 'info' },
  running: { label: 'Running', variant: 'info' },
  done: { label: 'Done', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
};
